/* global BigInt */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  formatMinorUnits,
  formatQuantity,
  moneyMinorValue,
  quantityIntegerValue
} from '../reportFormatters';
import { ComparisonControl, sampleAxisPoints } from './TrendPanel';
import { createChartTimeline } from '../reportChartTimeline';
import { REPORT_SERIES_COLORS } from '../reportChartPalette';

const WIDTH = 1000;
const HEIGHT = 260;
const BOUNDS = { left: 22, right: 988, top: 16, bottom: 232 };
const ANNUAL_MONTH_COUNT = 12;
const PRODUCT_GROUP_ORDER = ['certificates', 'signatures', 'archive'];
const ARCHIVE_GROUP_KEYS = new Set([
  'additional-downloading-documents',
  'additional-storage-electronic-archiving',
  'electronic-archiving-service'
]);
const SIGNATURE_GROUP_KEYS = new Set(['qualified-signatures', 'simple-signatures']);
const FINANCIAL_SERIES = [
  { key: 'total', labelId: 'components.pages.reports.summary.total', color: REPORT_SERIES_COLORS.total },
  ...PRODUCT_GROUP_ORDER.map(key => ({
    key,
    labelId: `components.pages.reports.summary.productGroup.${key}`,
    color: REPORT_SERIES_COLORS[key]
  }))
];

function chartBuckets(series, dateTo) {
  return (Array.isArray(series) ? series : []).filter(bucket => (
    !dateTo || !bucket.date_from || bucket.date_from <= dateTo
  ));
}

function bucketCalendarPart(bucket) {
  const match = /^(\d{4})-(\d{2})/.exec(bucket && bucket.date_from ? bucket.date_from : '');
  if (!match) return null;
  return { month: Number(match[2]), year: Number(match[1]) };
}

function chartBucketDomain(current, comparison, options = {}) {
  return createChartTimeline({
    annual: Boolean(options.annual),
    comparisonPeriod: options.comparisonPeriod,
    comparisonSeries: comparison,
    currentPeriod: options.currentPeriod,
    currentSeries: current,
    left: BOUNDS.left,
    right: BOUNDS.right
  }).buckets;
}

function slotBoundaryX(index, length) {
  return length > 0
    ? BOUNDS.left + ((index / length) * (BOUNDS.right - BOUNDS.left))
    : (BOUNDS.left + BOUNDS.right) / 2;
}

function xForIndex(index, length) {
  return slotBoundaryX(index + 0.5, length);
}

function barClusterGeometry({
  centerX,
  clusterGap = 5,
  fillRatio = 0.72,
  fixedBarWidth = null,
  intraPairGap = 1.5,
  maxBarWidth = 20,
  periodCount,
  productCount,
  step
}) {
  const safePeriods = Math.max(1, periodCount);
  const safeProducts = Math.max(1, productCount);
  const centeredPlotWidth = Math.max(0, 2 * Math.min(
    centerX - BOUNDS.left,
    BOUNDS.right - centerX
  ));
  const availableWidth = Math.min(step * fillRatio, centeredPlotWidth);
  const pairGapWidth = intraPairGap * Math.max(0, safePeriods - 1);
  const totalPairGap = pairGapWidth * safeProducts;
  const totalClusterGap = clusterGap * Math.max(0, safeProducts - 1);
  const calculatedBarWidth = Math.max(2, Math.min(
    maxBarWidth,
    Math.max(0, availableWidth - totalPairGap - totalClusterGap) / (safeProducts * safePeriods)
  ));
  const barWidth = fixedBarWidth === null
    ? calculatedBarWidth
    : Math.max(2, Math.min(maxBarWidth, fixedBarWidth));
  const clusterWidth = (safePeriods * barWidth) + pairGapWidth;
  const groupWidth = (safeProducts * clusterWidth) + totalClusterGap;
  return {
    barWidth,
    clusterGap,
    groupWidth,
    intraPairGap,
    x(productIndex, periodIndex) {
      return centerX
        - (groupWidth / 2)
        + (productIndex * (clusterWidth + clusterGap))
        + (periodIndex * (barWidth + intraPairGap));
    }
  };
}

function barVerticalGeometry(valueY, zeroY) {
  if (valueY === zeroY) {
    return { height: 1.5, y: zeroY - 0.75 };
  }
  return { height: Math.abs(zeroY - valueY), y: Math.min(zeroY, valueY) };
}

function xPercent(value) {
  return `${Number(((value / WIDTH) * 100).toFixed(4))}%`;
}

function niceIntegerStep(roughStep) {
  if (roughStep <= 1n) return 1n;
  const magnitude = BigInt(`1${'0'.repeat(String(roughStep).length - 1)}`);
  const candidates = [magnitude, magnitude * 2n];
  if (magnitude >= 2n) candidates.push((magnitude * 5n) / 2n);
  candidates.push(magnitude * 5n, magnitude * 10n);
  return candidates.find(candidate => candidate >= roughStep) || magnitude * 10n;
}

function buildNiceIntegerScale(rawValues, targetIntervals = 5n) {
  const values = rawValues.filter(value => typeof value === 'bigint');
  const minimumValue = values.reduce((minimum, value) => value < minimum ? value : minimum, 0n);
  const maximumValue = values.reduce((maximum, value) => value > maximum ? value : maximum, 0n);
  const extent = (-minimumValue) > maximumValue ? -minimumValue : maximumValue;
  const roughStep = extent === 0n
    ? 1n
    : (extent + targetIntervals - 1n) / targetIntervals;
  const step = niceIntegerStep(roughStep);
  const minimum = minimumValue < 0n
    ? -(((-minimumValue + step - 1n) / step) * step)
    : 0n;
  let maximum = maximumValue > 0n
    ? ((maximumValue + step - 1n) / step) * step
    : 0n;
  if (maximumValue > 0n && ((maximum - maximumValue) * 5n) < step) {
    maximum += step;
  }
  if (minimum === maximum) maximum = minimum + (step * targetIntervals);
  const range = maximum - minimum;
  const yForValue = value => Number((
    BigInt(BOUNDS.bottom) * 1000000n
    - (((value - minimum) * BigInt(BOUNDS.bottom - BOUNDS.top) * 1000000n) / range)
  )) / 1000000;
  const ticks = [];
  for (let value = minimum; value <= maximum; value += step) {
    ticks.push({ isZero: value === 0n, value, y: yForValue(value) });
  }
  return { maximum, minimum, step, ticks, usesBigInt: true, yForValue };
}

function isAnnualMonthDomain(buckets) {
  if (!Array.isArray(buckets) || buckets.length !== ANNUAL_MONTH_COUNT) return false;
  const parts = buckets.map(bucketCalendarPart);
  const referenceYear = parts[0] && parts[0].year;
  return Boolean(referenceYear) && parts.every((part, index) => (
    part && part.year === referenceYear && part.month === index + 1
  ));
}

function AxisLabels({ axisPoints, formatTick, scale, showEveryMonth, xAxisLabel, yAxisLabel }) {
  const xPoints = sampleAxisPoints(
    axisPoints,
    showEveryMonth || isAnnualMonthDomain(axisPoints.map(point => point.bucket)) ? axisPoints.length : 5
  );
  return (
    <>
      <ol aria-label={yAxisLabel} className="report-workspace-chart__y-axis">
        {scale.ticks.map((tick, index) => (
          <li key={`${String(tick.value)}:${index}`} style={{ top: `${(tick.y / HEIGHT) * 100}%` }}>
            {formatTick(tick.value)}
          </li>
        ))}
      </ol>
      <ol aria-label={xAxisLabel} className="report-workspace-chart__x-axis">
        {xPoints.map((point, index) => (
          <li
            className={[
              point.annualMonth ? 'annual-month' : '',
              point.axisLabelAnchor === 'center' ? 'centered' : '',
              point.axisLabelAnchor === 'start' || (!point.axisLabelAnchor && index === 0) ? 'first' : '',
              point.axisLabelAnchor === 'end' || (!point.axisLabelAnchor && index === xPoints.length - 1) ? 'last' : '',
              point.quarterBoundary ? 'quarter-boundary' : ''
            ].filter(Boolean).join(' ')}
            key={point.bucket.key}
            style={{ left: xPercent(point.x) }}
            title={point.bucket.label || point.bucket.key}
          >{point.bucket.label || point.bucket.key}</li>
        ))}
      </ol>
    </>
  );
}

function bucketPeriod(bucket) {
  if (!bucket) return '—';
  const dateFrom = bucket.date_from || '';
  const dateTo = bucket.date_to || '';
  if (dateFrom && dateTo && dateFrom.slice(0, 7) === dateTo.slice(0, 7)) {
    return dateFrom.slice(0, 7);
  }
  if (dateFrom && dateTo && dateFrom !== dateTo) {
    return `${dateFrom} – ${dateTo}`;
  }
  return dateFrom || dateTo || bucket.label || bucket.key || '—';
}

function buildTooltip(intl, { bucket, product, value, valueLabelId, x, y }) {
  const period = bucketPeriod(bucket);
  const productField = intl.formatMessage({ id: 'components.pages.reports.summary.tooltip.product' });
  const periodField = intl.formatMessage({ id: 'components.pages.reports.summary.tooltip.period' });
  const valueField = intl.formatMessage({ id: valueLabelId });
  return {
    ariaLabel: `${productField}: ${product}; ${periodField}: ${period}; ${valueField}: ${value}`,
    period,
    periodField,
    product,
    productField,
    value,
    valueField,
    x,
    y
  };
}

function tooltipHandlers(tooltip, setTooltip) {
  return {
    'aria-label': tooltip.ariaLabel,
    onBlur: () => setTooltip(null),
    onFocus: () => setTooltip(tooltip),
    onKeyDown: event => {
      if (event.key === 'Escape') setTooltip(null);
    },
    onMouseEnter: () => setTooltip(tooltip),
    onMouseLeave: () => setTooltip(null),
    role: 'img',
    tabIndex: 0
  };
}

function ChartTooltip({ tooltip }) {
  if (!tooltip) return null;
  const rawLeft = (tooltip.x / WIDTH) * 100;
  const left = Math.max(4, Math.min(96, rawLeft));
  const top = Math.max(8, Math.min(92, (tooltip.y / HEIGHT) * 100));
  const horizontal = rawLeft < 20 ? 'start' : rawLeft > 80 ? 'end' : 'center';
  const placement = tooltip.y < HEIGHT * 0.34 ? 'below' : 'above';
  return (
    <div
      className="report-workspace-chart__tooltip"
      data-horizontal={horizontal}
      data-placement={placement}
      role="tooltip"
      style={{ left: `${left}%`, top: `${top}%` }}
    >
      <span><strong>{tooltip.productField}:</strong> {tooltip.product}</span>
      <span><strong>{tooltip.periodField}:</strong> {tooltip.period}</span>
      <span><strong>{tooltip.valueField}:</strong> {tooltip.value}</span>
    </div>
  );
}

function ChartScaffold({ bucketGuides = false, children, formatTick, scale, timeline, tooltip, xAxisLabel, yAxisLabel }) {
  const bands = timeline.bands;
  return (
    <div className="report-workspace-chart">
      <AxisLabels
        axisPoints={timeline.axisPoints}
        formatTick={formatTick}
        scale={scale}
        showEveryMonth={timeline.bands.length > 1}
        xAxisLabel={xAxisLabel}
        yAxisLabel={yAxisLabel}
      />
      <div className="report-workspace-chart__plot">
        <svg aria-label={`${xAxisLabel}; ${yAxisLabel}`} preserveAspectRatio="none" role="img" viewBox={`0 0 ${WIDTH} ${HEIGHT}`}>
          <g aria-hidden="true" className="report-workspace-chart__quarter-bands">
            {bands.map(band => (
              <g data-quarter={band.key} key={band.key}>
                <rect
                  className={band.alternate ? 'alternate' : ''}
                  data-end={band.start + band.width}
                  data-start={band.start}
                  height={BOUNDS.bottom - BOUNDS.top}
                  width={band.width}
                  x={band.start}
                  y={BOUNDS.top}
                />
                <text x={band.start + 7} y={BOUNDS.top + 13}>{band.key.split('-')[1]}</text>
              </g>
            ))}
          </g>
          <g aria-hidden="true" className="report-workspace-chart__grid">
            {scale.ticks.map((tick, index) => (
              <line key={`${String(tick.value)}:${index}`} x1={BOUNDS.left} x2={BOUNDS.right} y1={tick.y} y2={tick.y}/>
            ))}
          </g>
          {bucketGuides && (
            <g aria-hidden="true" className="report-workspace-chart__bucket-guides">
              {timeline.axisPoints.map(point => (
                <line
                  data-bucket-guide={point.bucket.key}
                  key={point.bucket.key}
                  x1={point.x}
                  x2={point.x}
                  y1={BOUNDS.top}
                  y2={BOUNDS.bottom}
                />
              ))}
            </g>
          )}
          <line className="report-workspace-chart__axis" x1={BOUNDS.left} x2={BOUNDS.left} y1={BOUNDS.top} y2={BOUNDS.bottom}/>
          <line className="report-workspace-chart__axis" x1={BOUNDS.left} x2={BOUNDS.right} y1={BOUNDS.bottom} y2={BOUNDS.bottom}/>
          {children}
        </svg>
        <ChartTooltip tooltip={tooltip}/>
      </div>
    </div>
  );
}

function ToggleButton({ active, children, onClick }) {
  return (
    <button aria-pressed={active} className={active ? 'active' : ''} onClick={onClick} type="button">
      {children}
    </button>
  );
}

function SeriesSwatch({ color }) {
  return (
    <i
      aria-hidden="true"
      className="report-series-swatch"
      style={{ '--report-series-color': color }}
    />
  );
}

function TypeControls({ onChange, value }) {
  return (
    <fieldset className="report-chart-controls__type">
      <legend><FormattedMessage id="components.pages.reports.summary.chartType"/></legend>
      <div className="report-chart-toggle">
        <ToggleButton active={value === 'bars'} onClick={() => onChange('bars')}>Bars</ToggleButton>
        <ToggleButton active={value === 'lines'} onClick={() => onChange('lines')}>Lines</ToggleButton>
      </div>
    </fieldset>
  );
}

function productMoneyRows(bucket) {
  const rows = [];
  const groups = bucket && bucket.product_groups;
  const groupEntries = Array.isArray(groups)
    ? groups.map(group => [group && (group.key || group.id || group.uuid), group])
    : groups && typeof groups === 'object' ? Object.entries(groups) : [];
  groupEntries.forEach(([fallbackKey, group]) => {
    if (!group || typeof group !== 'object') return;
    const rawGroup = group.canonical_key || group.key || group.name || fallbackKey;
    const groupKey = canonicalProductGroup(rawGroup);
    const products = Array.isArray(group.products)
      ? group.products
      : group.products && typeof group.products === 'object' ? Object.values(group.products) : [];
    products.forEach(product => {
      if (!product || typeof product !== 'object') return;
      rows.push({ groupKey, value: moneyMinorValue(product.net_sales) });
    });
  });
  return rows;
}

function bucketIdentity(bucket) {
  return bucket && (bucket.date_from || bucket.key);
}

function chartBucketCenterX(timeline, bucket, bucketIndex, bucketCount) {
  const sourceBucket = bucket && (bucket.currentBucket || bucket.comparisonBucket);
  const identity = bucketIdentity(sourceBucket);
  const exactAxisPoint = timeline.axisPoints.find(point => (
    bucketIdentity(point.bucket) === identity
      || (sourceBucket && point.bucket && point.bucket.key === sourceBucket.key)
  ));
  if (exactAxisPoint) return exactAxisPoint.x;

  const month = sourceBucket && sourceBucket.date_from
    ? sourceBucket.date_from.slice(0, 7)
    : '';
  const monthAxisPoint = month && timeline.axisPoints.find(point => (
    point.bucket && (
      point.bucket.label === month
      || point.bucket.key === month
      || (point.bucket.date_from || '').slice(0, 7) === month
    )
  ));
  return monthAxisPoint
    ? monthAxisPoint.x
    : bucket.currentX ?? bucket.comparisonX ?? xForIndex(bucketIndex, bucketCount);
}

function normalizeProductMoneySeries(buckets, current, comparison) {
  const currentMap = new Map((Array.isArray(current) ? current : []).map(bucket => [bucketIdentity(bucket), bucket]));
  const comparisonMap = new Map((Array.isArray(comparison) ? comparison : []).map(bucket => [bucketIdentity(bucket), bucket]));
  const valuesFor = (period, groupKey) => buckets.map(bucket => {
    const targetBucket = bucket[`${period}Bucket`];
    if (!targetBucket) return null;
    const sourceBucket = (period === 'current' ? currentMap : comparisonMap).get(bucketIdentity(targetBucket));
    if (!sourceBucket) return null;
    const rows = productMoneyRows(sourceBucket).filter(row => row.groupKey === groupKey);
    return rows.length > 0 ? rows.reduce((total, row) => total + row.value, 0n) : null;
  });
  return PRODUCT_GROUP_ORDER.map(groupKey => ({
    current: valuesFor('current', groupKey),
    comparison: valuesFor('comparison', groupKey),
    key: groupKey
  }));
}

function useScopedSeriesSelection(availableKeys, defaultKeys = availableKeys) {
  const signature = availableKeys.join('|');
  const stableKeys = useMemo(() => signature.split('|').filter(Boolean), [signature]);
  const defaultSignature = defaultKeys.join('|');
  const stableDefaultKeys = useMemo(
    () => defaultSignature.split('|').filter(Boolean),
    [defaultSignature]
  );
  const previousKeys = useRef(stableKeys);
  const [selectedKeys, setSelectedKeys] = useState(() => {
    const defaults = stableKeys.filter(key => stableDefaultKeys.includes(key));
    return defaults.length > 0 ? defaults : stableKeys.slice(0, 1);
  });

  useEffect(() => {
    const previous = previousKeys.current;
    setSelectedKeys(current => {
      const retained = current.filter(key => stableKeys.includes(key));
      const newlyAvailable = stableKeys.filter(key => (
        !previous.includes(key) && stableDefaultKeys.includes(key)
      ));
      const next = stableKeys.filter(key => retained.includes(key) || newlyAvailable.includes(key));
      return next.length > 0 || stableKeys.length === 0 ? next : [stableKeys[0]];
    });
    previousKeys.current = stableKeys;
  }, [stableDefaultKeys, stableKeys]);

  return [selectedKeys, setSelectedKeys];
}

function FinancialChart({
  availableProductSeries = PRODUCT_GROUP_ORDER,
  annual,
  comparison,
  comparisonEnabled,
  comparisonPeriod,
  current,
  currentPeriod,
  dateTo,
  onComparisonChange,
  productComparison,
  productCurrent
}) {
  const intl = useIntl();
  const [chartType, setChartType] = useState('lines');
  const availableDefinitions = useMemo(() => FINANCIAL_SERIES.filter(item => (
    item.key === 'total' || availableProductSeries.includes(item.key)
  )), [availableProductSeries]);
  const [selected, setSelected] = useScopedSeriesSelection(
    availableDefinitions.map(item => item.key),
    ['total']
  );
  const [internalComparisonEnabled, setInternalComparisonEnabled] = useState(true);
  const showComparison = typeof comparisonEnabled === 'boolean'
    ? comparisonEnabled
    : internalComparisonEnabled;
  const [tooltip, setTooltip] = useState(null);
  const currentBuckets = chartBuckets(current, dateTo);
  const comparisonBuckets = Array.isArray(comparison) ? comparison : [];
  const timeline = createChartTimeline({
    annual,
    comparisonPeriod,
    comparisonSeries: comparisonBuckets,
    currentPeriod,
    currentSeries: currentBuckets,
    left: BOUNDS.left,
    right: BOUNDS.right
  });
  const buckets = timeline.buckets;
  const comparisonVisible = showComparison && comparisonBuckets.length > 0;
  const productGroups = normalizeProductMoneySeries(
    buckets,
    chartBuckets(productCurrent, dateTo),
    productComparison
  );
  const visibleSeries = availableDefinitions.filter(item => selected.includes(item.key));
  const definitions = visibleSeries.flatMap(item => {
    const group = productGroups.find(candidate => candidate.key === item.key);
    const valuesFor = period => item.key === 'total'
      ? buckets.map(bucket => bucket[`${period}Bucket`]
        ? moneyMinorValue(bucket[`${period}Bucket`].net_sales)
        : null)
      : group[period];
    return [
      { ...item, comparison: false, values: valuesFor('current') },
      ...(comparisonVisible
        ? [{ ...item, comparison: true, values: valuesFor('comparison') }]
        : [])
    ];
  });
  const allValues = definitions.flatMap(definition => definition.values).filter(value => value !== null);
  const scale = buildNiceIntegerScale(allValues.length > 0 ? allValues : [0n]);
  const formatTick = value => formatMinorUnits({
    currency: 'RON', without_vat_minor: String(value)
  });
  const toggleMetric = key => setSelected(values => (
    values.includes(key) && values.length > 1
      ? values.filter(value => value !== key)
      : values.includes(key) ? values : [...values, key]
  ));

  return (
    <section aria-labelledby="financial-chart-title" className="report-chart-workspace" data-testid="financial-chart-workspace">
      <div className="report-chart-workspace__header">
        <h2 id="financial-chart-title"><FormattedMessage id="components.pages.reports.summary.financialChart"/></h2>
        <div className="report-chart-controls">
          <fieldset className="report-chart-controls__series">
            <legend><FormattedMessage id="components.pages.reports.summary.series"/></legend>
            <div className="report-chart-toggle">
              {availableDefinitions.map(item => (
                <ToggleButton active={selected.includes(item.key)} key={item.key} onClick={() => toggleMetric(item.key)}>
                  <SeriesSwatch color={item.color}/>
                  <FormattedMessage id={item.labelId}/>
                </ToggleButton>
              ))}
            </div>
          </fieldset>
          <TypeControls onChange={setChartType} value={chartType}/>
          {comparisonBuckets.length > 0 && (
            <ComparisonControl
              checked={showComparison}
              onChange={checked => {
                if (typeof comparisonEnabled !== 'boolean') {
                  setInternalComparisonEnabled(checked);
                }
                if (onComparisonChange) {
                  onComparisonChange(checked);
                }
                setTooltip(null);
              }}
            />
          )}
        </div>
      </div>
      <div className="report-chart-legend">
        <span><i className="current"/><FormattedMessage id="components.pages.reports.summary.currentSeries"/></span>
        {comparisonVisible && <span><i className="comparison"/><FormattedMessage id="components.pages.reports.summary.comparisonSeries"/></span>}
      </div>
      {buckets.length === 0 ? (
        <div className="report-trend-panel__empty"><FormattedMessage id="components.pages.reports.summary.noTrend"/></div>
      ) : (
        <ChartScaffold
          formatTick={formatTick}
          scale={scale}
          timeline={timeline}
          tooltip={tooltip}
          xAxisLabel={intl.formatMessage({ id: 'components.pages.reports.summary.xAxis' })}
          yAxisLabel={intl.formatMessage({ id: 'components.pages.reports.summary.yAxisMoney' })}
        >
          {chartType === 'lines' ? definitions.map(definition => {
            const points = definition.values.map((value, index) => value === null ? null : ({
              bucket: buckets[index],
              sourceBucket: buckets[index][definition.comparison ? 'comparisonBucket' : 'currentBucket'],
              value,
              x: buckets[index][definition.comparison ? 'comparisonX' : 'currentX']
                ?? xForIndex(index, buckets.length),
              y: scale.yForValue(value)
            })).filter(Boolean);
            return (
              <g className={definition.comparison ? 'comparison' : ''} key={`${definition.key}:${definition.comparison}`}>
                <polyline
                  fill="none"
                  points={points.map(point => `${point.x},${point.y}`).join(' ')}
                  stroke={definition.color}
                />
                {points.map(point => {
                  const product = intl.formatMessage({ id: definition.labelId });
                  const value = formatTick(point.value);
                  const datumTooltip = buildTooltip(intl, {
                    bucket: point.sourceBucket,
                    product,
                    value,
                    valueLabelId: 'components.pages.reports.summary.tooltip.netValue',
                    x: point.x,
                    y: point.y
                  });
                  return (
                    <circle
                      cx={point.x}
                      cy={point.y}
                      key={point.bucket.key}
                      r="4"
                      stroke={definition.color}
                      {...tooltipHandlers(datumTooltip, setTooltip)}
                    />
                  );
                })}
              </g>
            );
          }) : definitions.map((definition, definitionIndex) => {
            const step = (BOUNDS.right - BOUNDS.left) / Math.max(1, buckets.length);
            const periodCount = comparisonVisible ? 2 : 1;
            const productIndex = Math.floor(definitionIndex / periodCount);
            const periodIndex = definitionIndex % periodCount;
            return definition.values.map((value, index) => {
              if (value === null) return null;
              const zeroY = scale.yForValue(0n);
              const y = scale.yForValue(value);
              const centerX = buckets[index][definition.comparison ? 'comparisonX' : 'currentX']
                ?? xForIndex(index, buckets.length);
              const geometry = barClusterGeometry({
                centerX,
                periodCount,
                productCount: visibleSeries.length,
                step
              });
              const x = geometry.x(productIndex, periodIndex);
              const vertical = barVerticalGeometry(y, zeroY);
              const product = intl.formatMessage({ id: definition.labelId });
              const formattedValue = formatTick(value);
              const datumTooltip = buildTooltip(intl, {
                bucket: buckets[index][definition.comparison ? 'comparisonBucket' : 'currentBucket'],
                product,
                value: formattedValue,
                valueLabelId: 'components.pages.reports.summary.tooltip.netValue',
                x: x + (geometry.barWidth / 2),
                y: vertical.y
              });
              return (
                <rect
                  className={definition.comparison ? 'comparison' : ''}
                  fill={definition.color}
                  height={vertical.height}
                  key={`${definition.key}:${definition.comparison}:${buckets[index].key}`}
                  stroke={definition.comparison ? definition.color : undefined}
                  width={geometry.barWidth}
                  x={x}
                  y={vertical.y}
                  {...tooltipHandlers(datumTooltip, setTooltip)}
                />
              );
            });
          })}
        </ChartScaffold>
      )}
    </section>
  );
}

function canonicalProductGroup(value) {
  const normalized = String(value || '').toLowerCase();
  if (ARCHIVE_GROUP_KEYS.has(normalized)) return 'archive';
  if (SIGNATURE_GROUP_KEYS.has(normalized)) return 'signatures';
  return 'certificates';
}

function macroProductSeriesKeys(series) {
  const keys = new Set();
  const addKey = value => {
    if (value !== null && value !== undefined && String(value).trim() !== '') {
      keys.add(canonicalProductGroup(value));
    }
  };
  (Array.isArray(series) ? series : []).forEach(bucket => {
    const groups = bucket && (bucket.product_groups || bucket.groups);
    const values = Array.isArray(groups)
      ? groups
      : groups && typeof groups === 'object' ? Object.entries(groups).map(([fallbackKey, group]) => ({
        ...(group && typeof group === 'object' ? group : {}),
        __fallbackKey: fallbackKey
      })) : [];
    values.forEach(group => addKey(
      group && (group.canonical_key || group.key || group.name || group.__fallbackKey)
    ));
    (Array.isArray(bucket && bucket.products) ? bucket.products : []).forEach(product => addKey(
      product && (product.product_group_key || product.group_key)
    ));
  });
  return PRODUCT_GROUP_ORDER.filter(key => keys.has(key));
}

function productRows(bucket) {
  const rows = [];
  const addProduct = (item, groupHint, fallbackKey) => {
    if (!item || typeof item !== 'object') return;
    const rawQuantity = item.fulfilled_quantity && typeof item.fulfilled_quantity === 'object'
      ? item.fulfilled_quantity.net
      : item.fulfilled_quantity ?? item.quantity ?? item.value;
    const parsedQuantity = quantityIntegerValue(rawQuantity);
    if (parsedQuantity === null) return;
    const quantity = parsedQuantity < 0n ? 0n : parsedQuantity;
    const groupEntity = groupHint && typeof groupHint === 'object' ? groupHint : null;
    const rawGroup = item.product_group_key
      || item.group_key
      || (groupEntity && (groupEntity.canonical_key || groupEntity.key || groupEntity.name))
      || groupHint
      || fallbackKey;
    const groupKey = canonicalProductGroup(rawGroup);
    rows.push({ groupKey, quantity });
  };
  const addGroup = (group, fallbackKey) => {
    if (!group || typeof group !== 'object') return;
    const products = group.products || group.items;
    if (Array.isArray(products)) {
      products.forEach(product => addProduct(product, group));
    } else if (products && typeof products === 'object') {
      Object.entries(products).forEach(([key, product]) => addProduct(product, group, key));
    } else {
      addProduct(group, group, fallbackKey);
    }
  };
  const groups = bucket && (bucket.product_groups || bucket.groups);
  if (Array.isArray(groups)) {
    groups.forEach(group => addGroup(group));
  } else if (groups && typeof groups === 'object') {
    Object.entries(groups).forEach(([key, group]) => addGroup(group, key));
  }
  if (Array.isArray(bucket && bucket.products)) {
    bucket.products.forEach(product => addProduct(product));
  }
  if (rows.length === 0 && bucket && bucket.quantities && typeof bucket.quantities === 'object') {
    Object.entries(bucket.quantities).forEach(([key, item]) => addProduct(
      typeof item === 'object' ? item : { fulfilled_quantity: item },
      key,
      key
    ));
  }
  return rows;
}

function normalizeProductSeries(current, comparison, domainBuckets) {
  const currentBuckets = Array.isArray(current) ? current : [];
  const comparisonBuckets = Array.isArray(comparison) ? comparison : [];
  const buckets = Array.isArray(domainBuckets) && domainBuckets.length > 0
    ? domainBuckets
    : chartBucketDomain(currentBuckets, comparisonBuckets);
  const sourceMaps = {
    currentBucket: new Map(currentBuckets.map(bucket => [bucketIdentity(bucket), bucket])),
    comparisonBucket: new Map(comparisonBuckets.map(bucket => [bucketIdentity(bucket), bucket]))
  };
  const valuesFor = (bucketType, groupKey) => buckets.map(bucket => {
    const domainBucket = bucket[bucketType];
    if (!domainBucket) return null;
    const productBucket = sourceMaps[bucketType].get(bucketIdentity(domainBucket));
    if (!productBucket) return null;
    const rows = productRows(productBucket).filter(row => row.groupKey === groupKey);
    return rows.length > 0 ? rows.reduce((total, row) => total + row.quantity, 0n) : null;
  });
  return {
    buckets,
    groups: PRODUCT_GROUP_ORDER.map(groupKey => ({
      color: REPORT_SERIES_COLORS[groupKey],
      current: valuesFor('currentBucket', groupKey),
      comparison: comparisonBuckets.length > 0 ? valuesFor('comparisonBucket', groupKey) : [],
      key: groupKey,
      labelId: `components.pages.reports.summary.productGroup.${groupKey}`
    }))
  };
}

function ProductChart({
  annual,
  availableProductSeries = PRODUCT_GROUP_ORDER,
  comparison,
  comparisonEnabled,
  comparisonPeriod,
  current,
  currentPeriod,
  dateTo,
  onComparisonChange
}) {
  const intl = useIntl();
  const [chartType, setChartType] = useState('bars');
  const [layout, setLayout] = useState('grouped');
  const [internalComparisonEnabled, setInternalComparisonEnabled] = useState(true);
  const showComparison = typeof comparisonEnabled === 'boolean'
    ? comparisonEnabled
    : internalComparisonEnabled;
  const [tooltip, setTooltip] = useState(null);
  const timeline = useMemo(() => createChartTimeline({
    annual,
    comparisonPeriod,
    comparisonSeries: comparison,
    currentPeriod,
    currentSeries: chartBuckets(current, dateTo),
    left: BOUNDS.left,
    right: BOUNDS.right
  }), [annual, comparison, comparisonPeriod, current, currentPeriod, dateTo]);
  const normalized = useMemo(() => normalizeProductSeries(
    chartBuckets(current, dateTo), comparison, timeline.buckets
  ), [comparison, current, dateTo, timeline.buckets]);
  const availableGroups = normalized.groups.filter(group => availableProductSeries.includes(group.key));
  const [selectedKeys, setSelectedKeys] = useScopedSeriesSelection(
    availableGroups.map(group => group.key),
    ['certificates', 'signatures']
  );
  const groups = availableGroups.filter(group => selectedKeys.includes(group.key));
  const comparisonAvailable = normalized.buckets.some(bucket => bucket.comparisonBucket);
  const comparisonVisible = comparisonAvailable && showComparison;
  const groupedPeriodCount = comparisonVisible ? 2 : 1;
  const groupedMaxBarWidth = 28 / Math.sqrt(Math.max(1, groups.length));
  const bucketStep = (BOUNDS.right - BOUNDS.left) / Math.max(1, normalized.buckets.length);
  const fixedGroupedBarWidth = normalized.buckets.reduce((width, bucket, bucketIndex) => {
    const centerX = chartBucketCenterX(
      timeline, bucket, bucketIndex, normalized.buckets.length
    );
    const candidate = barClusterGeometry({
      centerX,
      fillRatio: 0.82,
      maxBarWidth: groupedMaxBarWidth,
      periodCount: groupedPeriodCount,
      productCount: Math.max(1, groups.length),
      step: bucketStep
    }).barWidth;
    return width === null ? candidate : Math.min(width, candidate);
  }, null);
  const stackedSums = normalized.buckets.flatMap((bucket, bucketIndex) => (
    (comparisonVisible ? ['current', 'comparison'] : ['current']).map(period => {
      const values = groups.map(group => group[period][bucketIndex]).filter(value => (
        value !== null && value !== undefined
      ));
      return values.length > 0 ? values.reduce((total, value) => total + value, 0n) : null;
    })
  ));
  const allValues = layout === 'stacked' && chartType === 'bars'
    ? stackedSums
    : groups.flatMap(group => [
      ...group.current,
      ...(comparisonVisible ? group.comparison : [])
    ]);
  const chartValues = allValues.filter(value => value !== null);
  const scale = buildNiceIntegerScale(chartValues.length > 0 ? chartValues : [0n]);
  const toggleGroup = key => setSelectedKeys(values => (
    values.includes(key)
      ? values.length > 1 ? values.filter(value => value !== key) : values
      : [...values, key]
  ));

  return (
    <section aria-labelledby="product-chart-title" className="report-chart-workspace" data-testid="product-chart-workspace">
      <div className="report-chart-workspace__header">
        <h2 id="product-chart-title"><FormattedMessage id="components.pages.reports.summary.productChart"/></h2>
        <div className="report-chart-controls">
          <fieldset className="report-chart-controls__series report-chart-controls__products">
            <legend><FormattedMessage id="components.pages.reports.summary.series"/></legend>
            <div className="report-chart-toggle report-product-series-controls">
              {availableGroups.map(group => (
                <ToggleButton active={selectedKeys.includes(group.key)} key={group.key} onClick={() => toggleGroup(group.key)}>
                  <SeriesSwatch color={group.color}/>
                  <FormattedMessage id={group.labelId}/>
                </ToggleButton>
              ))}
            </div>
          </fieldset>
          <fieldset className="report-chart-controls__layout">
            <legend><FormattedMessage id="components.pages.reports.summary.layout"/></legend>
            <div className="report-chart-toggle">
              <ToggleButton active={layout === 'stacked'} onClick={() => setLayout('stacked')}><FormattedMessage id="components.pages.reports.summary.stacked"/></ToggleButton>
              <ToggleButton active={layout === 'grouped'} onClick={() => setLayout('grouped')}><FormattedMessage id="components.pages.reports.summary.grouped"/></ToggleButton>
            </div>
          </fieldset>
          <TypeControls onChange={setChartType} value={chartType}/>
          {comparisonAvailable && (
            <ComparisonControl
              checked={showComparison}
              onChange={checked => {
                if (typeof comparisonEnabled !== 'boolean') {
                  setInternalComparisonEnabled(checked);
                }
                if (onComparisonChange) {
                  onComparisonChange(checked);
                }
                setTooltip(null);
              }}
            />
          )}
        </div>
      </div>
      <div className="report-chart-legend">
        <span><i className="current"/><FormattedMessage id="components.pages.reports.summary.currentSeries"/></span>
        {comparisonVisible && <span><i className="comparison"/><FormattedMessage id="components.pages.reports.summary.comparisonSeries"/></span>}
      </div>
      {normalized.buckets.length === 0 ? (
        <div className="report-trend-panel__empty"><FormattedMessage id="components.pages.reports.summary.noTrend"/></div>
      ) : (
        <ChartScaffold
          bucketGuides
          formatTick={value => formatQuantity(value)}
          scale={scale}
          timeline={timeline}
          tooltip={tooltip}
          xAxisLabel={intl.formatMessage({ id: 'components.pages.reports.summary.xAxis' })}
          yAxisLabel={intl.formatMessage({ id: 'components.pages.reports.summary.yAxisQuantity' })}
        >
          {chartType === 'lines' ? groups.flatMap(group => [
            { comparison: false, values: group.current },
            ...(comparisonVisible ? [{ comparison: true, values: group.comparison }] : [])
          ].map(definition => {
            const points = definition.values.map((value, index) => value === null ? null : ({
              sourceBucket: normalized.buckets[index][definition.comparison ? 'comparisonBucket' : 'currentBucket'],
              value,
              x: normalized.buckets[index][definition.comparison ? 'comparisonX' : 'currentX']
                ?? xForIndex(index, normalized.buckets.length),
              y: scale.yForValue(value)
            })).filter(Boolean);
            return (
              <g className={definition.comparison ? 'comparison' : ''} key={`${group.key}:${definition.comparison}`}>
                <polyline fill="none" points={points.map(point => `${point.x},${point.y}`).join(' ')} stroke={group.color}/>
                {points.map((point, index) => {
                  const product = intl.formatMessage({ id: group.labelId });
                  const value = formatQuantity(point.value);
                  const datumTooltip = buildTooltip(intl, {
                    bucket: point.sourceBucket,
                    product,
                    value,
                    valueLabelId: 'components.pages.reports.summary.tooltip.netQuantity',
                    x: point.x,
                    y: point.y
                  });
                  return (
                    <circle
                      cx={point.x}
                      cy={point.y}
                      key={`${group.key}:${point.x}:${index}`}
                      r="4"
                      stroke={group.color}
                      {...tooltipHandlers(datumTooltip, setTooltip)}
                    />
                  );
                })}
              </g>
            );
          })) : normalized.buckets.flatMap((bucket, bucketIndex) => {
            const periods = comparisonVisible ? ['current', 'comparison'] : ['current'];
            const bucketGroups = groups.filter(group => periods.some(period => (
              group[period][bucketIndex] !== null && group[period][bucketIndex] !== undefined
            )));
            const centerX = chartBucketCenterX(
              timeline, bucket, bucketIndex, normalized.buckets.length
            );
            const geometry = barClusterGeometry({
              centerX,
              fillRatio: 0.82,
              fixedBarWidth: layout === 'grouped' ? fixedGroupedBarWidth : null,
              maxBarWidth: layout === 'grouped' ? groupedMaxBarWidth : 28,
              periodCount: periods.length,
              productCount: layout === 'stacked' ? 1 : bucketGroups.length,
              step: bucketStep
            });
            return periods.flatMap((period, periodIndex) => {
              if (layout === 'grouped') {
                return bucketGroups.map((group, groupIndex) => {
                  const value = group[period][bucketIndex];
                  if (value === null || value === undefined) return null;
                  const y = scale.yForValue(value);
                  const zeroY = scale.yForValue(0n);
                  const x = geometry.x(groupIndex, periodIndex);
                  const vertical = barVerticalGeometry(y, zeroY);
                  const product = intl.formatMessage({ id: group.labelId });
                  const formattedValue = formatQuantity(value);
                  const datumTooltip = buildTooltip(intl, {
                    bucket: bucket[`${period}Bucket`],
                    product,
                    value: formattedValue,
                    valueLabelId: 'components.pages.reports.summary.tooltip.netQuantity',
                    x: x + (geometry.barWidth / 2),
                    y: vertical.y
                  });
                  return (
                    <rect
                      className={period === 'comparison' ? 'comparison' : ''}
                      fill={group.color}
                      height={vertical.height}
                      key={`${bucket.key}:${period}:${group.key}`}
                      stroke={period === 'comparison' ? group.color : undefined}
                      width={geometry.barWidth}
                      x={x}
                      y={vertical.y}
                      {...tooltipHandlers(datumTooltip, setTooltip)}
                    />
                  );
                });
              }
              const baseX = geometry.x(0, periodIndex);
              let cumulative = 0n;
              return bucketGroups.filter(group => (
                group[period][bucketIndex] !== null && group[period][bucketIndex] !== undefined
              )).map(group => {
                const value = group[period][bucketIndex];
                const lowerY = scale.yForValue(cumulative);
                cumulative += value;
                const upperY = scale.yForValue(cumulative);
                const vertical = barVerticalGeometry(upperY, lowerY);
                const product = intl.formatMessage({ id: group.labelId });
                const formattedValue = formatQuantity(value);
                const datumTooltip = buildTooltip(intl, {
                  bucket: bucket[`${period}Bucket`],
                  product,
                  value: formattedValue,
                  valueLabelId: 'components.pages.reports.summary.tooltip.netQuantity',
                  x: baseX + (geometry.barWidth / 2),
                  y: vertical.y
                });
                return (
                  <rect
                    className={period === 'comparison' ? 'comparison' : ''}
                    fill={group.color}
                    height={vertical.height}
                    key={`${bucket.key}:${period}:${group.key}`}
                    stroke={period === 'comparison' ? group.color : undefined}
                    width={geometry.barWidth}
                    x={baseX}
                    y={vertical.y}
                    {...tooltipHandlers(datumTooltip, setTooltip)}
                  />
                );
              });
            });
          })}
        </ChartScaffold>
      )}
    </section>
  );
}

function SummaryCharts({
  chartSeriesScope,
  comparisonPeriod,
  comparisonSeries,
  currentPeriod,
  dateTo,
  financialComparisonEnabled,
  onFinancialComparisonChange,
  onProductComparisonChange,
  periodPreset,
  productComparisonEnabled,
  productSeries,
  series
}) {
  const productCurrent = productSeries && productSeries.current;
  const productComparison = productSeries && productSeries.comparison;
  const annual = periodPreset === 'ytd';
  const configuredProductSeries = chartSeriesScope && Array.isArray(chartSeriesScope.keys)
    ? chartSeriesScope.keys
    : PRODUCT_GROUP_ORDER;
  const payloadProductSeries = useMemo(() => macroProductSeriesKeys([
    ...(Array.isArray(productCurrent) ? productCurrent : []),
    ...(Array.isArray(productComparison) ? productComparison : []),
    ...(Array.isArray(series) ? series : []),
    ...(Array.isArray(comparisonSeries) ? comparisonSeries : [])
  ]), [comparisonSeries, productComparison, productCurrent, series]);
  const availableProductSeries = chartSeriesScope && chartSeriesScope.unresolved
    ? payloadProductSeries
    : configuredProductSeries;
  const productScopeRestricted = Boolean(chartSeriesScope && chartSeriesScope.restricted);
  return (
    <div className="report-summary-charts">
      <FinancialChart
        annual={annual}
        availableProductSeries={availableProductSeries}
        comparison={comparisonSeries}
        comparisonEnabled={financialComparisonEnabled}
        comparisonPeriod={comparisonPeriod}
        current={series}
        currentPeriod={currentPeriod}
        dateTo={dateTo}
        onComparisonChange={onFinancialComparisonChange}
        productComparison={productComparison}
        productCurrent={productCurrent}
        productScopeRestricted={productScopeRestricted}
      />
      <ProductChart
        annual={annual}
        availableProductSeries={availableProductSeries}
        comparison={productComparison}
        comparisonEnabled={productComparisonEnabled}
        comparisonPeriod={comparisonPeriod}
        current={productCurrent}
        currentPeriod={currentPeriod}
        dateTo={dateTo}
        onComparisonChange={onProductComparisonChange}
      />
    </div>
  );
}

export {
  canonicalProductGroup,
  buildNiceIntegerScale,
  barClusterGeometry,
  chartBucketDomain,
  chartBuckets,
  FinancialChart,
  isAnnualMonthDomain,
  macroProductSeriesKeys,
  normalizeProductMoneySeries,
  normalizeProductSeries,
  ProductChart,
  slotBoundaryX,
  SummaryCharts
};
